import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Auth } from '@freelancer/auth';
import {
  BackendDeleteResponse,
  BackendPushResponse,
  ContestEngagement,
  ContestEngagementCollection,
  ContestFee,
  ContestFeesCollection,
  ContestFile,
  ContestFilesCollection,
  ContestHasInvoice,
  ContestHasInvoicesCollection,
  ContestInterestedFreelancer,
  ContestInterestedFreelancersCollection,
  ContestViewContest,
  ContestViewContestsCollection,
  ContestViewEntriesCollection,
  ContestViewEntry,
  ContestViewEntryIds,
  ContestViewEntryIdsCollection,
  CONTEST_ENTRY_AWARD_STATUSES,
  Datastore,
  DatastoreCollection,
  DatastoreDocument,
  EntryIdFilterOption,
  EntryIdSortOption,
  OrderByDirection,
  pluckList,
  Query,
  Skill,
  SkillsCollection,
  Tax,
  TaxCollection,
  User,
  UsersCollection,
  VISIBLE_ENTRIES_DEFAULT_FILTER,
} from '@freelancer/datastore';
import { MessagingChat } from '@freelancer/messaging-chat';
import { Seo } from '@freelancer/seo';
import { BackgroundColor } from '@freelancer/ui/page-layout';
import { HOUR } from '@freelancer/ui/relative-time';
import { assertNever, isDefined, toNumber } from '@freelancer/utils';
import {
  ContestStatusApi,
  EntryStatusApi,
} from 'api-typings/contests/contests';
import {
  ContextTypeApi,
  ThreadTypeApi,
} from 'api-typings/messages/messages_types';
import * as Rx from 'rxjs';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import { ContestMultiAwardSelection } from '../contest-multi-award-modal/contest-multi-award-modal.types';

export enum EntryFilterOption {
  ALL_ENTRIES = 'All Entries',
  ACTIVE = 'Active',
  TOP = 'Top',
  // NO_FEEDBACK = 'No Feedback',
  REJECTED = 'Rejected',
  WITHDRAWN = 'Withdrawn',
}

export enum SearchContextType {
  ENTRANT = 'entrant',
  ENTRY = 'entry',
}

export interface SearchFilter {
  readonly type?: SearchContextType;
  readonly filter?: ReadonlyArray<number>;
}

export enum EntrySortOption {
  DEFAULT = 'Default',
  RATING = 'Rating',
  TIME_SUBMITTED = 'Time Submitted',
  // FREELANCER_RATING = 'Freelancer Rating',
  LIKES = 'Likes',
}

@Component({
  template: `
    <app-contest-view-header
      [contest$]="contest$"
      [engagement$]="engagement$"
      [isContestOwner$]="isContestOwner$"
      [awardedEntry$]="awardedEntry$"
      [hasInvoices$]="hasInvoices$"
    ></app-contest-view-header>
    <fl-page-layout [backgroundColor]="BackgroundColor.DARK">
      <fl-page-layout-single>
        <router-outlet></router-outlet>
      </fl-page-layout-single>
    </fl-page-layout>
  `,
})
export class ContestViewComponent implements OnInit, OnDestroy {
  BackgroundColor = BackgroundColor;

  readonly ENTRY_LIST_PAGE_SIZE = 24;
  readonly TOP_ENTRY_MIN_RATING = 4;

  readonly EXTEND_BANNER_CONTEST_MAX_HOURS = 24;
  readonly EXTEND_BANNER_MIN_ENTRY_COUNT = 20;

  readonly AWARD_BANNER_GRACE_PERIOD_DAYS = 30;

  contestDocument: DatastoreDocument<ContestViewContestsCollection>;
  contest$: Rx.Observable<ContestViewContest>;
  contestLoaded$: Rx.Observable<boolean>;
  contestError$: Rx.Observable<boolean>;

  contestFees$: Rx.Observable<ContestFee>;
  contestFeesLoaded$: Rx.Observable<boolean>;
  contestFeesError$: Rx.Observable<boolean>;

  contestFilesCollection: DatastoreCollection<ContestFilesCollection>;
  contestFiles$: Rx.Observable<ReadonlyArray<ContestFile>>;

  entriesCollection: DatastoreCollection<ContestViewEntriesCollection>;
  entries$: Rx.Observable<ReadonlyArray<ContestViewEntry>>;
  entriesLoaded$: Rx.Observable<boolean>;
  entriesError$: Rx.Observable<boolean>;

  entryIdsCollection: DatastoreCollection<ContestViewEntryIdsCollection>;
  entryIds$: Rx.Observable<ContestViewEntryIds>;
  engagement$: Rx.Observable<ContestEngagement>;

  currentPageSubject$ = new Rx.BehaviorSubject(1);
  currentPage$ = this.currentPageSubject$.asObservable();
  currentLimit$: Rx.Observable<number>;

  currentEntryFilterSubscription?: Rx.Subscription;
  currentEntryFilterSubject$ = new Rx.BehaviorSubject(
    EntryFilterOption.ALL_ENTRIES,
  );
  currentEntryFilter$ = this.currentEntryFilterSubject$
    .asObservable()
    .pipe(distinctUntilChanged());

  currentSearchFilterSubscription?: Rx.Subscription;
  private currentSearchFilterSubject$: Rx.BehaviorSubject<
    SearchFilter
  > = new Rx.BehaviorSubject({});
  currentSearchFilter$ = this.currentSearchFilterSubject$
    .asObservable()
    .pipe(distinctUntilChanged());

  currentEntrySortSubscription?: Rx.Subscription;
  currentEntrySortSubject$ = new Rx.BehaviorSubject(EntrySortOption.DEFAULT);
  currentEntrySort$ = this.currentEntrySortSubject$
    .asObservable()
    .pipe(distinctUntilChanged());

  visibleEntries$: Rx.Observable<ReadonlyArray<ContestViewEntry>>;
  numberOfPages$: Rx.Observable<number>;

  awardedEntry$: Rx.Observable<ContestViewEntry | undefined>;
  awardedEntryError$: Rx.Observable<boolean>;

  awardedFreelancer$: Rx.Observable<User | undefined>;
  awardedFreelancerError$: Rx.Observable<boolean>;

  freelancers$: Rx.Observable<ReadonlyArray<User>>;
  freelancersLoaded$: Rx.Observable<boolean>;
  freelancersError$: Rx.Observable<boolean>;

  interestedFreelancers$: Rx.Observable<
    ReadonlyArray<ContestInterestedFreelancer>
  >;
  userId$: Rx.Observable<number>;
  user$: Rx.Observable<User>;

  // This should probably be changed to check for collaborators too
  isContestOwner$: Rx.Observable<boolean>;
  tax$: Rx.Observable<Readonly<Tax>>;
  taxError$: Rx.Observable<boolean>;

  setTitleSubscription?: Rx.Subscription;

  timeLeftToAward$: Rx.Observable<number | null>;
  showAwardBanner$: Rx.Observable<boolean>;
  hasInvoices$: Rx.Observable<ContestHasInvoice>;

  showExtendBanner$: Rx.Observable<boolean>;

  skills$: Rx.Observable<ReadonlyArray<Skill>>;

  isOnMultiAwardSubject$ = new Rx.BehaviorSubject(false);
  isOnMultiAward$ = this.isOnMultiAwardSubject$.asObservable();

  multiAwardEntries: ReadonlyArray<ContestViewEntry> = [];
  multiAwardEntriesSubject$ = new Rx.BehaviorSubject<
    ReadonlyArray<ContestViewEntry>
  >([]);
  multiAwardEntries$ = this.multiAwardEntriesSubject$.asObservable();

  multiAwardFreelancers: ReadonlyArray<User> = [];
  multiAwardFreelancersSubject$ = new Rx.BehaviorSubject<ReadonlyArray<User>>(
    [],
  );
  multiAwardFreelancers$ = this.multiAwardFreelancersSubject$.asObservable();

  constructor(
    private auth: Auth,
    private activatedRoute: ActivatedRoute,
    private datastore: Datastore,
    private messagingChat: MessagingChat,
    private router: Router,
    private seo: Seo,
  ) {}

  ngOnInit() {
    this.userId$ = this.auth.getUserId().pipe(map(id => toNumber(id)));
    this.user$ = this.datastore
      .document<UsersCollection>('users', this.userId$)
      .valueChanges();

    const contestId$ = this.activatedRoute.params.pipe(
      map(params => toNumber(params.contestId)),
    );

    this.contestDocument = this.datastore.document<
      ContestViewContestsCollection
    >('contestViewContests', contestId$);
    this.contest$ = this.contestDocument.valueChanges();
    this.contestLoaded$ = this.contestDocument.status$.pipe(
      map(status => status.ready),
    );
    this.contestError$ = this.contestDocument.status$.pipe(
      map(status => !!status.error),
    );

    this.contestFilesCollection = this.datastore.collection<
      ContestFilesCollection
    >('contestFiles', query => query.where('contestId', '==', contestId$));
    this.contestFiles$ = this.contestFilesCollection.valueChanges();

    this.skills$ = this.datastore
      .collection<SkillsCollection>('skills')
      .valueChanges();

    this.currentLimit$ = this.currentPage$.pipe(
      map(currentPage => currentPage * this.ENTRY_LIST_PAGE_SIZE),
    );

    this.entriesCollection = this.datastore.collection<
      ContestViewEntriesCollection
    >('contestViewEntries', query =>
      Rx.combineLatest([
        this.currentEntryFilter$,
        this.currentEntrySort$,
        this.currentSearchFilter$,
      ]).pipe(
        map(([currentEntryFilter, currentEntrySort, currentSearchFilter]) => {
          let queryBuild = query.where('contestId', '==', contestId$);

          // Queries should be the same with
          // ContestProjectEntriesMySqlDAO::getFilteredAndSortedEntryIds
          switch (currentEntryFilter) {
            case EntryFilterOption.ALL_ENTRIES:
              queryBuild = queryBuild.where(
                'status',
                'in',
                VISIBLE_ENTRIES_DEFAULT_FILTER,
              );
              break;
            case EntryFilterOption.ACTIVE:
              queryBuild = queryBuild.where('status', 'in', [
                EntryStatusApi.PENDING,
                EntryStatusApi.ACTIVE,
                EntryStatusApi.WON,
                EntryStatusApi.WON_CLOSED,
                EntryStatusApi.WON_RUNNER_UP,
                EntryStatusApi.WON_BRONZE,
                EntryStatusApi.WON_SILVER,
                EntryStatusApi.WON_GOLD,
                EntryStatusApi.CHOSEN,
                EntryStatusApi.BOUGHT,
              ]);
              break;
            case EntryFilterOption.TOP:
              queryBuild = this.getTopRatedEntriesQuery(queryBuild);
              break;
            case EntryFilterOption.WITHDRAWN:
              queryBuild = queryBuild.where('status', 'in', [
                EntryStatusApi.WITHDRAWN,
                EntryStatusApi.WITHDRAWN_ELIMINATED,
              ]);
              break;
            case EntryFilterOption.REJECTED:
              queryBuild = queryBuild.where('status', 'in', [
                EntryStatusApi.ELIMINATED,
                EntryStatusApi.WITHDRAWN_ELIMINATED,
              ]);
              break;
            default:
              assertNever(currentEntryFilter);
          }

          switch (currentEntrySort) {
            case EntrySortOption.DEFAULT:
              break;
            case EntrySortOption.RATING:
              queryBuild = queryBuild
                .orderBy('rating', OrderByDirection.DESC)
                .orderBy('statusNumber', OrderByDirection.DESC)
                .orderBy('number', OrderByDirection.DESC);
              break;
            case EntrySortOption.TIME_SUBMITTED:
              queryBuild = queryBuild
                .orderBy('number', OrderByDirection.DESC)
                .orderBy('statusNumber', OrderByDirection.DESC)
                .orderBy('rating', OrderByDirection.DESC);
              break;
            case EntrySortOption.LIKES:
              queryBuild = queryBuild
                .orderBy('likeCount', OrderByDirection.DESC)
                .orderBy('statusNumber', OrderByDirection.DESC)
                .orderBy('rating', OrderByDirection.DESC)
                .orderBy('number', OrderByDirection.DESC);
              break;
            default:
              assertNever(currentEntrySort);
          }

          if (
            currentSearchFilter &&
            currentSearchFilter.type &&
            currentSearchFilter.filter
          ) {
            switch (currentSearchFilter.type) {
              case SearchContextType.ENTRY:
                queryBuild = queryBuild.where(
                  'id',
                  'in',
                  currentSearchFilter.filter,
                );
                break;
              case SearchContextType.ENTRANT:
                queryBuild = queryBuild.where(
                  'ownerId',
                  'in',
                  currentSearchFilter.filter,
                );
                break;
              default:
                break;
            }
          }

          return queryBuild.limit(this.currentLimit$);
        }),
      ),
    );

    this.entries$ = this.entriesCollection.valueChanges();
    this.entriesLoaded$ = this.entriesCollection.status$.pipe(
      map(status => status.ready),
    );
    this.entriesError$ = this.entriesCollection.status$.pipe(
      map(status => !!status.error),
    );

    this.visibleEntries$ = Rx.combineLatest([
      this.entries$,
      this.currentPage$,
      this.currentLimit$,
    ]).pipe(
      map(([entries, currentPage, currentLimit]) =>
        entries.slice(
          (currentPage - 1) * this.ENTRY_LIST_PAGE_SIZE,
          currentLimit,
        ),
      ),
    );

    this.entryIdsCollection = this.datastore.collection<
      ContestViewEntryIdsCollection
    >('contestViewEntryIds', query =>
      Rx.combineLatest([
        this.currentEntryFilter$,
        this.currentEntrySort$,
        this.currentSearchFilter$,
      ]).pipe(
        map(([currentEntryFilter, currentEntrySort, currentSearchFilter]) => {
          let queryBuild = query.where('contestId', '==', contestId$);

          switch (currentEntryFilter) {
            case EntryFilterOption.ALL_ENTRIES:
              queryBuild = queryBuild.where(
                'filter',
                '==',
                EntryIdFilterOption.DEFAULT,
              );
              break;
            case EntryFilterOption.ACTIVE:
              queryBuild = queryBuild.where(
                'filter',
                '==',
                EntryIdFilterOption.ACTIVE,
              );
              break;
            case EntryFilterOption.TOP:
              queryBuild = queryBuild.where(
                'filter',
                '==',
                EntryIdFilterOption.TOP,
              );
              break;
            case EntryFilterOption.WITHDRAWN:
              queryBuild = queryBuild.where(
                'filter',
                '==',
                EntryIdFilterOption.WITHDRAWN,
              );
              break;
            case EntryFilterOption.REJECTED:
              queryBuild = queryBuild.where(
                'filter',
                '==',
                EntryIdFilterOption.REJECTED,
              );
              break;
            default:
              assertNever(currentEntryFilter);
          }

          switch (currentEntrySort) {
            case EntrySortOption.DEFAULT:
              queryBuild = queryBuild.where(
                'sort',
                '==',
                EntryIdSortOption.DEFAULT,
              );
              break;
            case EntrySortOption.RATING:
              queryBuild = queryBuild.where(
                'sort',
                '==',
                EntryIdSortOption.RATING,
              );
              break;
            case EntrySortOption.TIME_SUBMITTED:
              queryBuild = queryBuild.where(
                'sort',
                '==',
                EntryIdSortOption.TIME_SUBMITTED,
              );
              break;
            case EntrySortOption.LIKES:
              queryBuild = queryBuild.where(
                'sort',
                '==',
                EntryIdSortOption.LIKES,
              );
              break;
            default:
              assertNever(currentEntrySort);
          }

          if (
            currentSearchFilter &&
            currentSearchFilter.type &&
            currentSearchFilter.filter
          ) {
            switch (currentSearchFilter.type) {
              case SearchContextType.ENTRY:
                queryBuild = queryBuild.search({
                  entryIds: currentSearchFilter.filter,
                });
                break;
              case SearchContextType.ENTRANT:
                queryBuild = queryBuild.where(
                  'entryOwnerIds',
                  '==',
                  currentSearchFilter.filter,
                );
                break;
              default:
                break;
            }
          }

          return queryBuild;
        }),
      ),
    );

    this.entryIds$ = this.entryIdsCollection.valueChanges().pipe(
      map(contestEntryIds => contestEntryIds[0]),
      filter(isDefined),
    );

    this.currentEntryFilterSubscription = this.currentEntryFilter$.subscribe(
      () => this.jumpToPage(1),
    );

    this.currentSearchFilterSubscription = this.currentSearchFilter$.subscribe(
      () => this.jumpToPage(1),
    );

    this.currentEntrySortSubscription = this.currentEntrySort$.subscribe(e => {
      this.jumpToPage(1);
    });

    this.numberOfPages$ = this.entryIds$.pipe(
      map(contestViewEntryIds =>
        Math.ceil(
          contestViewEntryIds.entryIds.length / this.ENTRY_LIST_PAGE_SIZE,
        ),
      ),
    );

    const engagementDocument = this.datastore.document<
      ContestEngagementCollection
    >('contestEngagement', contestId$);
    this.engagement$ = engagementDocument.valueChanges();

    const awardedEntriesCollection = this.datastore.collection<
      ContestViewEntriesCollection
    >('contestViewEntries', query =>
      query
        .where('contestId', '==', contestId$)
        .where('status', 'in', CONTEST_ENTRY_AWARD_STATUSES),
    );
    // Getting the first awarded entry is done on the observable instead of
    // in the datastore query because `orderBy` doesn't support nullable field.
    // `timeWon` is a nullable field.
    // FIXME: Sort in datastore query after T84381 is implemented
    this.awardedEntry$ = awardedEntriesCollection.valueChanges().pipe(
      map(
        awardedEntries =>
          [...awardedEntries].sort((previousEntry, currentEntry) => {
            if (previousEntry.timeWon && currentEntry.timeWon) {
              return previousEntry.timeWon - currentEntry.timeWon;
            }
            if (previousEntry.timeWon) {
              return -1;
            }
            if (currentEntry.timeWon) {
              return 1;
            }
            return 0;
          })[0],
      ),
    );
    this.awardedEntryError$ = awardedEntriesCollection.status$.pipe(
      map(status => !!status.error),
    );

    const awardedFreelancerDocument = this.datastore.document<UsersCollection>(
      'users',
      this.awardedEntry$.pipe(
        filter(isDefined),
        map(awardedEntry => awardedEntry.ownerId),
      ),
    );
    this.awardedFreelancer$ = awardedFreelancerDocument.valueChanges();
    this.awardedFreelancerError$ = awardedFreelancerDocument.status$.pipe(
      map(status => !!status.error),
    );

    this.isContestOwner$ = Rx.combineLatest([this.userId$, this.contest$]).pipe(
      map(([userId, contest]) => userId === contest.ownerId),
    );

    // ds.objects() doesn't expose a status$ stream so we opted to use ds.collection here
    const freelancersCollection = this.datastore.collection<UsersCollection>(
      'users',
      query =>
        query.where(
          'id',
          'in',
          this.visibleEntries$.pipe(pluckList('ownerId')),
        ),
    );
    this.freelancers$ = freelancersCollection.valueChanges();
    this.freelancersLoaded$ = freelancersCollection.status$.pipe(
      map(status => status.ready),
    );
    this.freelancersError$ = freelancersCollection.status$.pipe(
      map(status => !!status.error),
    );

    const currencyId$ = this.contest$.pipe(map(contest => contest.currency.id));

    const contestFeesCollection = this.datastore.collection<
      ContestFeesCollection
    >('contestFees', query =>
      query
        .where('contestId', '==', contestId$)
        .where('currencyId', '==', currencyId$),
    );

    this.contestFees$ = contestFeesCollection.valueChanges().pipe(
      map(contestFees => contestFees[0]),
      filter(isDefined),
    );
    this.contestFeesLoaded$ = contestFeesCollection.status$.pipe(
      map(status => status.ready),
    );

    this.contestFeesError$ = contestFeesCollection.status$.pipe(
      map(status => !!status.error),
    );

    const taxDocument = this.datastore.document<TaxCollection>(
      'tax',
      this.userId$,
    );
    this.tax$ = taxDocument.valueChanges();
    this.taxError$ = taxDocument.status$.pipe(map(status => !!status.error));

    const isNavigationEndEvent$ = this.router.events.pipe(
      map(event => event instanceof NavigationEnd),
      startWith(true),
    );

    const emitForSetTitle$ = Rx.combineLatest([
      this.contest$.pipe(map(contest => contest.title)),
      isNavigationEndEvent$.pipe(filter(value => value)),
    ]).pipe(map(([title, _]) => title));

    this.setTitleSubscription = emitForSetTitle$.subscribe(title =>
      this.seo.setPageTags({ title }),
    );

    const currentTimestamp = Date.now();

    const hasTopRatedEntries$ = this.datastore
      .collection<ContestViewEntriesCollection>('contestViewEntries', query => {
        let queryBuild = query.where('contestId', '==', contestId$);
        queryBuild = this.getTopRatedEntriesQuery(queryBuild).limit(1);
        return queryBuild;
      })
      .valueChanges()
      .pipe(map(topRatedEntryArray => topRatedEntryArray.length !== 0));

    this.showExtendBanner$ = Rx.combineLatest([
      this.isContestOwner$,
      this.awardedEntry$,
      this.contest$,
      hasTopRatedEntries$,
    ]).pipe(
      map(([isContestOwner, awardedEntry, contest, hasTopRatedEntries]) => {
        if (
          contest.timeEnded === undefined ||
          contest.entryCount === undefined
        ) {
          return false;
        }

        const timeRemainingInHours =
          (contest.timeEnded - currentTimestamp) / (HOUR * 1000);

        return (
          isContestOwner &&
          !awardedEntry &&
          (!contest.upgrades || !contest.upgrades.urgent) &&
          timeRemainingInHours > 0 &&
          timeRemainingInHours <= this.EXTEND_BANNER_CONTEST_MAX_HOURS &&
          (contest.entryCount > this.EXTEND_BANNER_MIN_ENTRY_COUNT ||
            !hasTopRatedEntries)
        );
      }),
    );

    this.timeLeftToAward$ = this.contest$.pipe(
      map(contest => {
        if (contest.timeEnded) {
          const awardDeadline = new Date(contest.timeEnded);
          awardDeadline.setDate(
            awardDeadline.getDate() + this.AWARD_BANNER_GRACE_PERIOD_DAYS,
          );
          return awardDeadline.getTime() - currentTimestamp;
        }

        return null;
      }),
    );
    this.showAwardBanner$ = Rx.combineLatest([
      this.isContestOwner$,
      this.awardedEntry$,
      this.contest$,
      this.timeLeftToAward$,
    ]).pipe(
      map(
        ([isContestOwner, awardedEntry, contest, timeLeftToAward]) =>
          !!(
            isContestOwner &&
            !awardedEntry &&
            contest.status === ContestStatusApi.PENDING &&
            contest.timeEnded &&
            currentTimestamp >= contest.timeEnded &&
            timeLeftToAward &&
            timeLeftToAward > 0 &&
            contest.entryCount &&
            contest.entryCount > 0
          ),
      ),
    );

    this.hasInvoices$ = this.datastore
      .document<ContestHasInvoicesCollection>('contestHasInvoices', contestId$)
      .valueChanges();
  }

  getSearchFilter() {
    return this.currentSearchFilterSubject$.getValue();
  }

  getTopRatedEntriesQuery(query: Query<ContestViewEntriesCollection>) {
    return query
      .where('status', 'in', [
        EntryStatusApi.PENDING,
        EntryStatusApi.ACTIVE,
        EntryStatusApi.WON_RUNNER_UP,
        EntryStatusApi.WON_BRONZE,
        EntryStatusApi.WON_SILVER,
        EntryStatusApi.WON_GOLD,
      ])
      .where('rating', '>=', this.TOP_ENTRY_MIN_RATING);
  }

  jumpToPage(pageNumber: number) {
    this.currentPageSubject$.next(pageNumber);
  }

  setEntryFilter(entryFilter: EntryFilterOption) {
    this.currentEntryFilterSubject$.next(entryFilter);
  }

  setSearchFilter(searchFilter: SearchFilter) {
    this.currentSearchFilterSubject$.next(searchFilter);
  }

  setEntrySort(entrySort: EntrySortOption) {
    this.currentEntrySortSubject$.next(entrySort);
  }

  startChat(entry: ContestViewEntry, origin: string) {
    this.messagingChat.startChat({
      userIds: [entry.ownerId],
      threadType: ThreadTypeApi.PRIVATE_CHAT,
      origin,
      context: {
        type: ContextTypeApi.CONTEST,
        id: entry.contestId,
      },
    });
  }

  /**
   * We don't want to retrieve unnecessary data so we do lazy loading for
   * interestedFreelancers.
   *
   * @returns Rx.Observable<ReadonlyArray<ContestInterestedFreelancer>>
   */
  getInterestedFreelancersObservable(): Rx.Observable<
    ReadonlyArray<ContestInterestedFreelancer>
  > {
    if (this.interestedFreelancers$ === undefined) {
      this.interestedFreelancers$ = this.datastore
        .collection<ContestInterestedFreelancersCollection>(
          'contestInterestedFreelancers',
          query =>
            query.where(
              'contestId',
              '==',
              this.contest$.pipe(map(contest => contest.id)),
            ),
        )
        .valueChanges();
    }

    return this.interestedFreelancers$;
  }

  updateContestDetails(contestChanges: Partial<ContestViewContest>) {
    return this.contestDocument.update(contestChanges);
  }

  updateContestFiles(
    newFiles: ReadonlyArray<ContestFile>,
    deletedFileIds: ReadonlyArray<number>,
  ): Promise<
    ReadonlyArray<
      | BackendPushResponse<ContestFilesCollection>
      | BackendDeleteResponse<ContestFilesCollection>
    >
  > {
    let createPromises: ReadonlyArray<
      Promise<BackendPushResponse<ContestFilesCollection>>
    > = [];
    let deletePromises: ReadonlyArray<
      Promise<BackendDeleteResponse<ContestFilesCollection>>
    > = [];

    if (newFiles) {
      createPromises = newFiles.map(file =>
        this.datastore.createDocument<ContestFilesCollection>(
          'contestFiles',
          file,
        ),
      );
    }

    if (deletedFileIds) {
      deletePromises = deletedFileIds.map(id =>
        this.contestFilesCollection.remove(id),
      );
    }

    return Promise.all([...createPromises, ...deletePromises]);
  }

  handleMultiAwardSelectToggle(
    contestMultiAwardSelection: ContestMultiAwardSelection,
  ) {
    if (contestMultiAwardSelection.selected) {
      this.addMultiAwardEntry(contestMultiAwardSelection);
    } else if (this.multiAwardEntries.length > 1) {
      this.removeMultiAwardEntry(contestMultiAwardSelection);
    } else {
      this.handleMultiAwardCancel();
    }
  }

  addMultiAwardEntry(contestMultiAwardSelection: ContestMultiAwardSelection) {
    this.multiAwardEntries = [
      ...this.multiAwardEntries,
      contestMultiAwardSelection.entry,
    ];
    this.multiAwardEntriesSubject$.next(this.multiAwardEntries);

    this.multiAwardFreelancers = [
      ...this.multiAwardFreelancers,
      contestMultiAwardSelection.freelancer,
    ];
    this.multiAwardFreelancersSubject$.next(this.multiAwardFreelancers);
  }

  removeMultiAwardEntry(
    contestMultiAwardSelection: ContestMultiAwardSelection,
  ) {
    // Multi-award entries and their respective freelancers are mapped
    // via matching indexes
    const removeElementIndex = this.multiAwardEntries.indexOf(
      contestMultiAwardSelection.entry,
    );

    this.multiAwardEntries = this.multiAwardEntries.filter(
      entry => entry.id !== contestMultiAwardSelection.entry.id,
    );
    this.multiAwardEntriesSubject$.next(this.multiAwardEntries);

    // Ensures removal of only one instance of the freelancer per
    // one removed multi-awarded entry since `multiAwardFreelancers`
    // can contain non-unique elements, e.g. some multi-awarded entries
    // belonging to one user
    this.multiAwardFreelancers = this.multiAwardFreelancers
      .slice(0, removeElementIndex)
      .concat(this.multiAwardFreelancers.slice(removeElementIndex + 1));
    this.multiAwardFreelancersSubject$.next(this.multiAwardFreelancers);
  }

  handleMultiAwardCancel() {
    this.isOnMultiAwardSubject$.next(false);

    this.multiAwardEntries = [];
    this.multiAwardEntriesSubject$.next(this.multiAwardEntries);

    this.multiAwardFreelancers = [];
    this.multiAwardFreelancersSubject$.next(this.multiAwardFreelancers);
  }

  handleMultiAwardShow() {
    this.isOnMultiAwardSubject$.next(true);
  }

  ngOnDestroy() {
    if (this.setTitleSubscription) {
      this.setTitleSubscription.unsubscribe();
    }

    if (this.currentEntryFilterSubscription) {
      this.currentEntryFilterSubscription.unsubscribe();
    }

    if (this.currentEntrySortSubscription) {
      this.currentEntrySortSubscription.unsubscribe();
    }

    if (this.currentSearchFilterSubscription) {
      this.currentSearchFilterSubscription.unsubscribe();
    }
  }
}
